import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getStoredContext } from '@/auth/sessionStorage';
import { createOrganisation, queryOrganisations } from '@/features/organisations/data';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import {
  createProject as createProjectRequest,
  getProjectById,
  queryProjects,
} from '@/features/projects/data';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { useNotifications } from '@/providers/notificationHooks';
import { gridPaths, invalidateCurrentUserRoles, invalidateGridQuery, queryKeys } from './queryKeys';
import {
  buildScopedStorageKey,
  clearBootstrapSnapshot,
  mergeProjectsById,
  readCachedProjectOptions,
  resolveBootstrapCacheSeed,
  resolveBootstrapUserId,
  toProjectOption,
  upsertProjectById,
  upsertProjectOptionById,
  upsertProjectQueryResult,
  writeBootstrapProjectOptionCaches,
  writeBootstrapSnapshot,
  writeCachedOrganisations,
  writeCachedProjectOptions,
  writeProjectStateCaches,
} from './useBootstrapData.cache';
import { useBootstrapDataEvents } from './useBootstrapDataEvents';
import { useInvalidateQueries } from './useInvalidateQueries';
import type {
  OrganisationOption,
  ProjectOption,
  UseBootstrapDataArgs,
} from './useBootstrapData.types';
import type { OrganisationDto, ProjectDto, QueryResult } from '@flowqa/shared';

const ORG_FILTER_KEY = 'flowqa-organisation-filter';
const PROJECT_FILTER_KEY = 'flowqa-project-filter';

export const useBootstrapData = ({
  session,
  enabled = true,
}: UseBootstrapDataArgs): {
  projectsLoaded: boolean;
  projects: ProjectDto[];
  organisations: Array<Pick<OrganisationDto, 'id' | 'name' | 'slug' | 'clerkOrganizationId'>>;
  visibleProjects: ProjectDto[];
  visibleProjectOptions: Array<
    Pick<ProjectDto, 'id' | 'name' | 'slug' | 'badgeChars' | 'badgeColor' | 'badgeEnabled'>
  >;
  createProject: (
    input: {
      name: string;
      badgeEnabled?: boolean;
      badgeChars?: ProjectDto['badgeChars'];
      badgeColor?: ProjectDto['badgeColor'];
    },
    options?: { setAsCurrent?: boolean },
  ) => Promise<ProjectDto | null>;
  createOrg: (name: string) => Promise<OrganisationDto | null>;
} => {
  const { notify } = useNotifications();
  const { selectedOrganisationId, setSelectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const bootstrapUserId = resolveBootstrapUserId(session);
  const initialCacheSeed = resolveBootstrapCacheSeed({
    bootstrapUserId,
    selectedOrganisationId,
    session,
  });
  const activeOrganisationId = initialCacheSeed.resolvedOrganisationId;
  const [projects, setProjects] = useState<ProjectDto[]>(initialCacheSeed.initialProjects);
  const [cachedProjectOptions, setCachedProjectOptions] = useState<ProjectOption[]>(
    () => initialCacheSeed.initialCachedProjectOptions,
  );
  const [organisations, setOrganisations] = useState<OrganisationOption[]>(
    () => initialCacheSeed.initialOrganisations,
  );
  const [hasLoadedProjects, setHasLoadedProjects] = useState(
    initialCacheSeed.freshSnapshot !== null,
  );
  const loadSeq = useRef(0);
  const lastUserIdRef = useRef<string | null>(null);
  const invalidateQueries = useInvalidateQueries();
  const queryClient = useQueryClient();

  useBootstrapDataEvents({
    organisations,
    selectedOrganisationId,
    selectedProjectId,
    userId: session?.userId,
    notify,
    queryClient,
    setOrganisations,
    setProjects,
    setCachedProjectOptions,
    setSelectedProjectId,
  });

  useEffect(() => {
    const userId = session?.userId;
    if (!enabled || userId === undefined) {
      return;
    }

    if (selectedOrganisationId === null) {
      return;
    }

    const refreshBillingState = (): void => {
      void invalidateQueries([
        { queryKey: queryKeys.organisationDetail(selectedOrganisationId) },
        { queryKey: queryKeys.organisationEntitlements(selectedOrganisationId) },
      ]);
    };

    const handleVisibilityChange = (): void => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      refreshBillingState();
    };

    window.addEventListener('online', refreshBillingState);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('online', refreshBillingState);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, invalidateQueries, selectedOrganisationId, session?.userId]);

  useEffect(() => {
    const nextUserId = session?.userId ?? null;
    if (lastUserIdRef.current === nextUserId) return;
    lastUserIdRef.current = nextUserId;
    const nextOrganisationId = initialCacheSeed.allowCachedBootstrap
      ? (session?.lastOrganisationId ?? session?.orgId ?? null)
      : null;
    const nextProjectId = initialCacheSeed.allowCachedBootstrap
      ? (session?.lastProjectId ?? getStoredContext().lastProjectId ?? null)
      : null;
    const cacheSeed = resolveBootstrapCacheSeed({
      bootstrapUserId: nextUserId,
      selectedOrganisationId: nextOrganisationId,
      session,
    });
    setProjects(cacheSeed.initialProjects);
    setOrganisations(cacheSeed.initialOrganisations);
    setCachedProjectOptions(cacheSeed.initialCachedProjectOptions);
    setHasLoadedProjects(cacheSeed.freshSnapshot !== null);
    setSelectedOrganisationId(nextOrganisationId);
    setSelectedProjectId(nextProjectId);
  }, [
    initialCacheSeed.allowCachedBootstrap,
    session,
    session?.lastOrganisationId,
    session?.lastProjectId,
    session?.orgId,
    session?.userId,
    setSelectedOrganisationId,
    setSelectedProjectId,
  ]);

  useEffect(() => {
    if (!session?.userId) return;
    setCachedProjectOptions(readCachedProjectOptions(activeOrganisationId, session.userId));
  }, [activeOrganisationId, session?.userId, setCachedProjectOptions]);

  useEffect(() => {
    const loadProjects = async (): Promise<void> => {
      if (!enabled) return;
      const runId = ++loadSeq.current;
      setHasLoadedProjects(false);
      if (!session?.userId) {
        setProjects([]);
        setOrganisations([]);
        setCachedProjectOptions([]);
        setHasLoadedProjects(true);
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(buildScopedStorageKey('flowqa-org-options-cache'));
          window.localStorage.removeItem(buildScopedStorageKey('flowqa-project-options-cache'));
          window.localStorage.removeItem(buildScopedStorageKey(ORG_FILTER_KEY));
          window.localStorage.removeItem(buildScopedStorageKey(PROJECT_FILTER_KEY));
        }
        clearBootstrapSnapshot();
        return;
      }
      const cacheSeed = resolveBootstrapCacheSeed({
        bootstrapUserId: session.userId,
        selectedOrganisationId,
        session,
      });
      const { freshSnapshot, persistedSnapshot, resolvedOrganisationId } = cacheSeed;
      if (freshSnapshot !== null) {
        setOrganisations(freshSnapshot.organisations);
        setProjects(freshSnapshot.projects);
        setCachedProjectOptions(readCachedProjectOptions(resolvedOrganisationId, session.userId));
        setHasLoadedProjects(true);
        queryClient.setQueryData(
          queryKeys.organisations(session.userId),
          freshSnapshot.organisations,
        );
        queryClient.setQueryData(queryKeys.projects(resolvedOrganisationId), {
          items: freshSnapshot.projects,
          total: freshSnapshot.projects.length,
          page: 1,
          pageSize: Math.max(freshSnapshot.projects.length, 1),
        });
      }
      try {
        const orgs = await queryOrganisations();
        const projectRequests = [
          { orgId: resolvedOrganisationId, promise: queryProjects(resolvedOrganisationId) },
        ];
        const projectPromises: Array<ReturnType<typeof queryProjects>> = [];
        projectRequests.forEach((request) => {
          projectPromises.push(request.promise);
        });
        const projectResults = await Promise.allSettled(projectPromises);
        if (runId !== loadSeq.current) return;
        setOrganisations(orgs.items);
        writeCachedOrganisations(orgs.items, session.userId);
        const successfulProjectResults = projectResults.flatMap((result) =>
          result.status === 'fulfilled' ? [result.value] : [],
        );
        const mergedProjects = mergeProjectsById(
          successfulProjectResults.flatMap((result) => result.items),
        );
        const hasRejectedProjectQuery = projectResults.some(
          (result) => result.status === 'rejected',
        );
        const rememberedProjectId =
          selectedProjectId ?? session.lastProjectId ?? getStoredContext().lastProjectId ?? null;
        const rememberedOrganisationId = resolvedOrganisationId;
        const rememberedProject =
          rememberedProjectId !== null &&
          !mergedProjects.some((project) => project.id === rememberedProjectId)
            ? await getProjectById(
                rememberedProjectId,
                rememberedOrganisationId ?? undefined,
              ).catch(() => null)
            : null;
        const refreshedProjects = rememberedProject
          ? mergeProjectsById([...mergedProjects, rememberedProject])
          : mergedProjects;
        const shouldPreserveSnapshotProjects =
          refreshedProjects.length === 0 &&
          persistedSnapshot !== null &&
          persistedSnapshot.projects.length > 0 &&
          (hasRejectedProjectQuery ||
            (rememberedProjectId !== null &&
              persistedSnapshot.projects.some((project) => project.id === rememberedProjectId)));
        const resolvedProjects = shouldPreserveSnapshotProjects
          ? persistedSnapshot.projects
          : refreshedProjects;
        setProjects(resolvedProjects);
        const nextProjectOptions = resolvedProjects.map(toProjectOption);
        setCachedProjectOptions(nextProjectOptions);
        writeBootstrapProjectOptionCaches(resolvedProjects, session.userId);
        writeBootstrapSnapshot(
          {
            savedAt: Date.now(),
            organisations: orgs.items,
            projects: resolvedProjects,
          },
          session.userId,
        );
        queryClient.setQueryData(queryKeys.organisations(session.userId), orgs.items);
        projectResults.forEach((result, index) => {
          if (result.status !== 'fulfilled') {
            return;
          }
          const orgId = projectRequests[index]?.orgId ?? null;
          queryClient.setQueryData(queryKeys.projects(orgId), result.value);
        });
        setHasLoadedProjects(true);
      } catch {
        if (runId !== loadSeq.current) return;
        const fallbackSeed = resolveBootstrapCacheSeed({
          bootstrapUserId: session.userId,
          selectedOrganisationId,
          session,
        });
        const fallbackOrganisationId = fallbackSeed.resolvedOrganisationId;
        const fallbackSnapshot = fallbackSeed.persistedSnapshot;
        const fallbackProjects = fallbackSnapshot
          ? fallbackSnapshot.projects.filter(
              (project) => project.organisationId === fallbackOrganisationId,
            )
          : [];
        const fallbackOrganisations = fallbackSeed.initialOrganisations;
        setProjects(fallbackProjects);
        setOrganisations(fallbackOrganisations);
        setCachedProjectOptions(
          fallbackSnapshot
            ? fallbackProjects.map(toProjectOption)
            : readCachedProjectOptions(fallbackOrganisationId, session.userId),
        );
        setHasLoadedProjects(true);
      }
    };
    void loadProjects();
  }, [enabled, queryClient, selectedOrganisationId, session?.userId]);

  const createProject = useCallback(
    async (
      input: {
        name: string;
        badgeEnabled?: boolean;
        badgeChars?: ProjectDto['badgeChars'];
        badgeColor?: ProjectDto['badgeColor'];
      },
      options?: { setAsCurrent?: boolean },
    ): Promise<ProjectDto | null> => {
      if (!session?.userId) return null;
      const created = await createProjectRequest(input, selectedOrganisationId);
      const nextProjects = upsertProjectById(projects, created);
      setProjects(nextProjects);
      queryClient.setQueryData<QueryResult<ProjectDto>>(
        queryKeys.projects(created.organisationId ?? null),
        (prev) => upsertProjectQueryResult(prev, created),
      );
      if (created.organisationId === selectedOrganisationId) {
        setCachedProjectOptions((prev) => {
          const next = upsertProjectOptionById(prev, toProjectOption(created));
          writeCachedProjectOptions(selectedOrganisationId, next, session.userId);
          return next;
        });
      }
      writeProjectStateCaches({
        projects: nextProjects,
        organisations,
        userId: session.userId,
      });
      setHasLoadedProjects(true);
      if (options?.setAsCurrent !== false) {
        setSelectedProjectId(created.id);
      }
      notify({ message: 'Project created', tone: 'green' });
      await invalidateQueries([
        invalidateGridQuery(gridPaths.projects),
        invalidateCurrentUserRoles(),
      ]);
      return created;
    },
    [
      invalidateQueries,
      notify,
      organisations,
      projects,
      queryClient,
      selectedOrganisationId,
      session?.userId,
      setSelectedProjectId,
    ],
  );

  const createOrg = useCallback(
    async (name: string): Promise<OrganisationDto | null> => {
      if (!session?.userId) return null;
      const created = await createOrganisation({ name });
      notify({ message: 'Workspace created', tone: 'green' });
      const orgs = await queryOrganisations();
      setOrganisations(orgs.items);
      writeCachedOrganisations(orgs.items, session.userId);
      setSelectedOrganisationId(created.id);
      setSelectedProjectId(null);
      await invalidateQueries([
        invalidateGridQuery(gridPaths.organisations),
        invalidateGridQuery(gridPaths.projects),
        invalidateGridQuery(gridPaths.users),
        invalidateCurrentUserRoles(),
      ]);
      return created;
    },
    [invalidateQueries, notify, session?.userId, setSelectedOrganisationId, setSelectedProjectId],
  );

  const visibleProjects = useMemo(() => {
    if (selectedOrganisationId === null) {
      return projects;
    }
    return projects.filter((p) => p.organisationId === selectedOrganisationId);
  }, [projects, selectedOrganisationId]);

  const visibleProjectOptions = useMemo(() => {
    const currentOptions = visibleProjects.map((project) => ({
      id: project.id,
      name: project.name,
      slug: project.slug,
      badgeChars: project.badgeChars,
      badgeColor: project.badgeColor,
      badgeEnabled: project.badgeEnabled,
    }));
    if (currentOptions.length > 0 || hasLoadedProjects) {
      return currentOptions;
    }
    return cachedProjectOptions.map((project) => ({
      id: project.id,
      name: project.name,
      slug: project.slug,
      badgeChars: project.badgeChars,
      badgeColor: project.badgeColor,
      badgeEnabled: project.badgeEnabled,
    }));
  }, [cachedProjectOptions, hasLoadedProjects, visibleProjects]);

  return {
    projectsLoaded: hasLoadedProjects,
    projects,
    organisations,
    visibleProjects,
    visibleProjectOptions,
    createProject,
    createOrg,
  };
};
